import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  formatDateTime,
  formatDuration,
  formatPhoneNumber,
} from '@/components/manager/analytics/drilldown/formatters'
import type { DrilldownCallRow } from '@/types/analyticsDrilldown'

interface Props {
  inProgress: DrilldownCallRow[]
  recent: DrilldownCallRow[]
  serverTime: string
  locale: string
  darkMode: boolean
}

function isMissed(call: DrilldownCallRow): boolean {
  return (
    call.direction === 'inbound' &&
    (call.status === 'missed' || call.status === 'no-answer')
  )
}

// Compteur "live" pour les calls in progress — affiche le temps écoulé depuis
// `started_at` avec un tick chaque seconde. Évite de recharger le snapshot
// juste pour l'affichage de la durée.
//
// Important : le `serverOffset` doit être figé au moment où on reçoit le
// snapshot (useMemo sur serverTimeIso). Sinon il est recalculé à chaque
// render et `now + serverOffset` reste constant → l'affichage est figé.
function useLiveElapsed(startedAtIso: string, serverTimeIso: string): string {
  const serverOffset = useMemo(
    () => new Date(serverTimeIso).getTime() - Date.now(),
    [serverTimeIso],
  )
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const elapsedMs = Math.max(0, now + serverOffset - new Date(startedAtIso).getTime())
  const elapsedSec = Math.floor(elapsedMs / 1000)
  const m = Math.floor(elapsedSec / 60)
  const s = elapsedSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Panneau standard : en-tête filet bas, contenu en dessous. */
function Panel({
  title,
  subtitle,
  darkMode,
  children,
  flush = false,
}: {
  title: string
  subtitle: string
  darkMode: boolean
  children: React.ReactNode
  /** true = le contenu colle aux bords (fil d'activité), false = padding (cartes). */
  flush?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-[10px] border shadow-sm overflow-hidden',
        darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
      )}
    >
      <div
        className={cn(
          'px-4 py-3 border-b',
          darkMode ? 'border-slate-700' : 'border-slate-200',
        )}
      >
        <div
          className={cn(
            'text-[13px] font-bold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {title}
        </div>
        <div
          className={cn(
            'text-xs mt-px',
            darkMode ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {subtitle}
        </div>
      </div>
      <div className={flush ? undefined : 'p-3 space-y-2'}>{children}</div>
    </div>
  )
}

export function LiveCallsList({ inProgress, recent, serverTime, locale, darkMode }: Props) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <Panel
        title={t('manager.live.inProgressTitle')}
        subtitle={t('manager.live.inProgressCount', { count: inProgress.length })}
        darkMode={darkMode}
      >
        {inProgress.length === 0 ? (
          <p
            className={cn(
              'text-[13px] py-6 text-center',
              darkMode ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            {t('manager.live.noInProgress')}
          </p>
        ) : (
          inProgress.map((call) => (
            <ActiveCallCard
              key={call.id}
              call={call}
              serverTime={serverTime}
              darkMode={darkMode}
            />
          ))
        )}
      </Panel>

      <Panel
        title={t('manager.live.recentTitle')}
        subtitle={t('manager.live.recentCount', { count: recent.length })}
        darkMode={darkMode}
        flush
      >
        {recent.length === 0 ? (
          <p
            className={cn(
              'text-[13px] py-6 text-center',
              darkMode ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            {t('manager.live.noRecent')}
          </p>
        ) : (
          <div
            className={cn(
              'divide-y',
              darkMode ? 'divide-slate-700/60' : 'divide-slate-100',
            )}
          >
            {recent.map((call) => (
              <RecentCallRow key={call.id} call={call} locale={locale} darkMode={darkMode} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  )
}

/** Étiquette ENTRANT / SORTANT / MANQUÉ — pilule compacte en capitales. */
function Tag({ call, darkMode }: { call: DrilldownCallRow; darkMode: boolean }) {
  const { t } = useTranslation()
  const missed = isMissed(call)
  const inbound = call.direction === 'inbound'
  const label = missed
    ? t('manager.live.row.directionMissed')
    : t(`manager.live.row.${inbound ? 'directionInbound' : 'directionOutbound'}`)
  return (
    <span
      className={cn(
        'inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide flex-shrink-0',
        missed
          ? darkMode
            ? 'bg-rose-500/15 text-rose-300'
            : 'bg-rose-50 text-rose-600'
          : inbound
            ? darkMode
              ? 'bg-emerald-500/15 text-emerald-300'
              : 'bg-emerald-50 text-emerald-600'
            : darkMode
              ? 'bg-indigo-500/15 text-indigo-300'
              : 'bg-indigo-50 text-indigo-600',
      )}
    >
      {label}
    </span>
  )
}

/** Sous-ligne « numéro · pris par X · reçu sur Y » factorisée. */
function CallMeta({
  call,
  darkMode,
  showNumber,
}: {
  call: DrilldownCallRow
  darkMode: boolean
  showNumber: boolean
}) {
  const { t } = useTranslation()
  const isInbound = call.direction === 'inbound'
  const peerPretty = formatPhoneNumber(call.peer_number)
  const callizyNumberRaw = isInbound ? call.to_number : call.from_number
  const callizyDisplay = call.callizy_number_label || formatPhoneNumber(callizyNumberRaw)
  const sep = <span className={darkMode ? 'text-slate-600' : 'text-slate-300'}>·</span>

  return (
    <div
      className={cn(
        'text-xs flex items-center gap-1.5 flex-wrap mt-px',
        darkMode ? 'text-slate-500' : 'text-slate-400',
      )}
    >
      {showNumber && peerPretty && (
        <>
          <span className="tabular-nums">{peerPretty}</span>
          {sep}
        </>
      )}
      {call.answered_by ? (
        <span>{t('manager.live.row.answeredBy', { name: call.answered_by.name })}</span>
      ) : (
        <span className="italic">{t('manager.live.row.noAgent')}</span>
      )}
      {callizyDisplay && (
        <>
          {sep}
          <span className={call.callizy_number_label ? '' : 'tabular-nums'}>
            {t(`manager.live.row.${isInbound ? 'receivedOn' : 'sentFrom'}`, {
              number: callizyDisplay,
            })}
          </span>
        </>
      )}
    </div>
  )
}

/** Appel actif : carte fine, point vert pulsant, minuterie mm:ss à droite. */
function ActiveCallCard({
  call,
  serverTime,
  darkMode,
}: {
  call: DrilldownCallRow
  serverTime: string
  darkMode: boolean
}) {
  const elapsed = useLiveElapsed(call.date, serverTime)
  const peerLabel =
    call.contact?.name ||
    formatPhoneNumber(call.peer_number)

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-[10px] border px-3.5 py-3',
        darkMode ? 'border-slate-700' : 'border-slate-200',
      )}
    >
      <span className="relative inline-flex h-2 w-2 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Tag call={call} darkMode={darkMode} />
          <span
            className={cn(
              'text-[13px] font-semibold truncate',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {peerLabel}
          </span>
        </div>
        <CallMeta call={call} darkMode={darkMode} showNumber={!!call.contact?.name} />
      </div>
      <span
        className={cn(
          'text-[13px] font-bold tabular-nums flex-shrink-0',
          darkMode ? 'text-emerald-300' : 'text-emerald-700',
        )}
      >
        {elapsed}
      </span>
    </div>
  )
}

/** Appel récent : ligne de fil plate — carré de statut, texte, heure à droite. */
function RecentCallRow({
  call,
  locale,
  darkMode,
}: {
  call: DrilldownCallRow
  locale: string
  darkMode: boolean
}) {
  const missed = isMissed(call)
  const inbound = call.direction === 'inbound'
  const square = missed ? 'bg-rose-500' : inbound ? 'bg-emerald-500' : 'bg-indigo-500'
  const peerLabel =
    call.contact?.name ||
    formatPhoneNumber(call.peer_number)

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-2.5',
        darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50',
      )}
    >
      <span className={cn('h-[7px] w-[7px] rounded-[2px] flex-shrink-0', square)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Tag call={call} darkMode={darkMode} />
          <span
            className={cn(
              'text-[13px] font-semibold truncate',
              darkMode ? 'text-slate-100' : 'text-slate-900',
            )}
          >
            {peerLabel}
          </span>
        </div>
        <CallMeta call={call} darkMode={darkMode} showNumber={!!call.contact?.name} />
      </div>
      <div className="flex flex-col items-end flex-shrink-0 text-xs tabular-nums">
        <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>
          {formatDateTime(call.date, locale)}
        </span>
        {call.duration_seconds != null && call.duration_seconds > 0 && (
          <span
            className={cn(
              'font-semibold',
              darkMode ? 'text-slate-300' : 'text-slate-600',
            )}
          >
            {formatDuration(call.duration_seconds)}
          </span>
        )}
      </div>
    </div>
  )
}
